import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useAutoSave } from '@hooks/useAutoSave';
import { Button, Stack, Switch, TableCell, TableRow, TextField, Typography } from '@mui/material';

import { MAX_LABEL_LENGTH, requirementKindLabel } from '../constants';
import type { DefinitionResponse, SubjectLanguage } from '../types';

export interface RequirementDefinitionRowProps {
  definition: DefinitionResponse;
  /** Rejects to let the row surface the failure and roll its control back. */
  /**
   * Bulk launch for THIS definition, supplied by the container (AC-10). A
   * node rather than a flag, so this component keeps knowing nothing about
   * permissions, queries, or the preview.
   */
  bulkLaunch?: ReactNode;
  onUpdate: (patch: {
    label?: string;
    active?: boolean;
    default_for_new_patients?: boolean;
  }) => Promise<void>;
  subject: SubjectLanguage;
  disabled?: boolean;
}

/**
 * One catalog row: the patient-facing label (AC-1) plus the active and
 * new-patient-default switches (PRD §3 — labels, active state, new-patient
 * defaults).
 *
 * The label is written ONLY when the admin commits — blur or Enter — never
 * while they type (GDN-3525). The editor promises cancellation, and a
 * background save behind that promise can break it: a keystroke debounce fired
 * during any pause longer than its window, so an edit the admin then abandoned
 * with Escape had already been sent and could not be un-sent. Blur still
 * commits, so tabbing or clicking away loses nothing; only *pausing* stopped
 * being a commit.
 *
 * This is a deliberate product decision, not a refactor — autosave-while-typing
 * is gone for this field. The ruling, and the five guarantees that replace the
 * row's previous test contract, are recorded here:
 * https://linear.app/jotpsych/issue/GDN-3525#comment-c8101cd8-9627-4f8e-ab39-a5fb5f6ef1ec
 */
export const RequirementDefinitionRow = ({
  definition,
  onUpdate,
  subject,
  bulkLaunch,
  disabled = false,
}: RequirementDefinitionRowProps) => {
  const [label, setLabel] = useState(definition.label);
  /** Text typed into the open editor and not yet committed or discarded. */
  const draftLabelRef = useRef<string | null>(null);
  /** Value of a save that has been issued and not yet confirmed. */
  const savingLabelRef = useRef<string | null>(null);
  /**
   * What the field showed when the editor opened — the target a discard
   * restores, and what a commit is compared against to decide it is a no-op.
   *
   * Deliberately not `definition.label`: while an earlier save is still in
   * flight the prop is the pre-save value, so comparing against it would treat
   * re-opening and closing without typing as a fresh edit and send that same
   * value a second time.
   */
  const editorOpenedWithRef = useRef(definition.label);
  /**
   * The input exists only while editing (GDN-3365). At rest the cell is plain
   * text, so the table reads as a list rather than a form — including for
   * keyboard and screen-reader users, which is why the PM ruled against a
   * borderless always-focusable input.
   */
  const [editing, setEditing] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /**
   * Set when the editor is closed by a key that does NOT move focus itself.
   *
   * Tab has already computed the next focus target before the input unmounts,
   * and a click away lands focus wherever it was clicked. Enter and Escape do
   * neither — so removing the focused input drops focus to `document.body` and
   * a keyboard user loses their place in the page entirely. This puts them
   * back on the control they just used (jotreviewer, #2982).
   */
  const restoreFocusRef = useRef(false);

  useEffect(() => {
    if (editing || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    // After the re-render that unmounted the input and put the trigger back.
    triggerRef.current?.focus();
  }, [editing]);

  // Adopt server-side label changes (another admin, another tab) unless this
  // row has an uncommitted edit or an unconfirmed save that adopting would
  // clobber.
  useEffect(() => {
    if (draftLabelRef.current === null && savingLabelRef.current === null) {
      setLabel(definition.label);
    }
  }, [definition.label]);

  const saveLabel = useCallback(
    async (next: string) => {
      await onUpdate({ label: next });
      // Clear the marker only if it still refers to the value that just saved.
      // An older request settling after the admin has committed again would
      // otherwise mark the newer text as confirmed, and the effect above would
      // then adopt the older server label over what is on screen. A rejection
      // leaves the marker set on purpose: the screen and the server disagree
      // until the admin resolves it.
      if (savingLabelRef.current === next) {
        savingLabelRef.current = null;
      }
    },
    [onUpdate],
  );

  /*
    `useAutoSave` is kept for what it does besides debouncing: it serialises
    writes on one tail, so a slow save can never be overtaken by a later one,
    and it owns the status the row displays. `triggerAutoSave` is deliberately
    not used — see the component comment.

    `flushOnUnmount` goes with it rather than being left set: it flushes only
    what `triggerAutoSave` recorded, so with no debounce it could never fire.
    Unmounting mid-edit removes the focused input, and the blur that follows is
    the commit — the same single route as every other exit.
  */
  const { saveImmediately, status, lastSavedAt } = useAutoSave<string>({
    onSave: saveLabel,
  });

  const handleLabelChange = (next: string) => {
    setLabel(next);
    draftLabelRef.current = next;
  };

  const openEditor = () => {
    editorOpenedWithRef.current = label;
    setEditing(true);
  };

  /** The single commit route. Enter reaches it by blurring the field. */
  const handleLabelBlur = () => {
    setEditing(false);
    const draft = draftLabelRef.current;
    draftLabelRef.current = null;
    if (draft === null) return;

    const openedWith = editorOpenedWithRef.current;
    if (draft.trim() === '' || draft === openedWith) {
      // An empty label would leave the patient page with an unnamed item, and
      // the API rejects it, so restore rather than save blank. An unchanged
      // one is a no-op: committing it would be a write that changes nothing.
      setLabel(openedWith);
      return;
    }

    savingLabelRef.current = draft;
    saveImmediately(draft);
  };

  /** Escape abandons the edit: nothing was written, so nothing is undone. */
  const handleLabelDiscard = () => {
    draftLabelRef.current = null;
    setLabel(editorOpenedWithRef.current);
    setEditing(false);
  };

  const kindName = requirementKindLabel(definition.kind);

  return (
    <>
      <TableRow>
      <TableCell sx={{ verticalAlign: 'top', width: '32%' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {kindName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {definition.kind === 'payment_method'
            ? `Built in. Satisfied when the ${subject.singular} has a saved card.`
            : `Satisfied when the ${subject.singular} provides this item.`}
        </Typography>
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {editing ? (
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={label}
              onChange={event => handleLabelChange(event.target.value)}
              onBlur={handleLabelBlur}
              onKeyDown={event => {
                // Enter commits through the same blur path, so there is one
                // save route rather than two that can disagree.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  restoreFocusRef.current = true;
                  /*
                    Blur the field rather than calling the handler: `onBlur` is
                    then the ONLY route to a save, which is what the previous
                    comment here claimed and the code did not do.

                    Calling it directly saved twice (jotreviewer, #2982): the
                    real blur that fires when the focused input unmounts ran
                    the handler again on a draft the first call had not
                    finished clearing.
                  */
                  // `target`, not `currentTarget`: MUI hangs onKeyDown on the
                  // root element, so currentTarget is a div that cannot blur.
                  (event.target as HTMLInputElement).blur();
                  return;
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  restoreFocusRef.current = true;
                  handleLabelDiscard();
                }
              }}
              disabled={disabled}
              label={`Name ${subject.plural} see`}
              slotProps={{ htmlInput: { maxLength: MAX_LABEL_LENGTH } }}
            />
          ) : (
            /*
              A real button, not a click handler on text: the rest state has to
              stay reachable by keyboard and announce itself, or click-to-edit
              would trade the screen-reader problem for an unreachable one
              (PM's MUST on this ruling). The accessible name carries the
              requirement, so a screen reader in a table of these can tell the
              rows apart.
            */
            <Button
              ref={triggerRef}
              onClick={openEditor}
              disabled={disabled}
              /*
                The accessible name STARTS with the visible text, then adds the
                purpose and the requirement.
                
                An `aria-label` of only "Edit …" would replace the name a voice
                user can see: someone saying "click Card on file" — the words
                actually on screen — would not match anything, which is a WCAG
                2.5.3 (Label in Name) failure at Level A. Containing the
                visible text fixes that while still naming the requirement, so
                a screen reader moving through a table of these can tell the
                rows apart.
              */
              aria-label={`${label}, edit ${subject.singular}-facing label for ${kindName}`}
              sx={{
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                justifyContent: 'flex-start',
                color: 'text.primary',
                fontWeight: 400,
              }}
            >
              {label}
            </Button>
          )}
          {/*
            Shown while editing and while any save is unsettled — including a
            failure after the input has closed, which must never be invisible.
            Silent at rest: the plain text showing the saved value IS the
            confirmation, and a table of rows each announcing "Saved" is the
            visual weight this redesign removes. PM ruling, logged there.
          */}
          {(editing || savingLabelRef.current !== null || status === 'error') && (
            <AutosaveStatus
              status={status}
              isDirty={draftLabelRef.current !== null || savingLabelRef.current !== null}
              lastSavedAt={lastSavedAt}
            />
          )}
        </Stack>
      </TableCell>

      <TableCell align="center" sx={{ verticalAlign: 'top', width: '14%' }}>
        <Switch
          checked={definition.active}
          disabled={disabled}
          onChange={event => {
            void onUpdate({ active: event.target.checked });
          }}
          slotProps={{ input: { 'aria-label': `${kindName} active` } }}
        />
      </TableCell>

      <TableCell align="center" sx={{ verticalAlign: 'top', width: '18%' }}>
        <Switch
          checked={definition.default_for_new_patients}
          disabled={disabled}
          onChange={event => {
            void onUpdate({ default_for_new_patients: event.target.checked });
          }}
          slotProps={{
            input: { 'aria-label': `${kindName} assigned to new ${subject.plural}` },
          }}
        />
      </TableCell>
      </TableRow>

      {/*
        A second full-width row rather than a fifth cell: the preview is a
        panel, and squeezing it into the actions column would either clip it or
        stretch every row to its height.

        Rendered only when the caller supplies something. The catalog view is
        presentational and does not know what a bulk launch is; its container
        does, and passes it down. That keeps the RTK Query calls out of a
        component whose tests and stories render it with no store.
      */}
      {bulkLaunch && (
        <TableRow>
          <TableCell colSpan={4} sx={{ pt: 0, borderBottom: 0 }}>
            {bulkLaunch}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
